import { useEffect, useState, useCallback, useRef } from "react";
import { useApp } from "../lib/store";
import { api, fmtDate, errDetail } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import {
  Upload, FileText, Loader2, Trash2, RefreshCw, AlertTriangle, CheckCircle2,
  HelpCircle, ScrollText, ChevronLeft, Quote,
} from "lucide-react";
import { TranslateBlock } from "./TranslateBlock";

const STATUS_STYLE = {
  match: "bg-emerald-50 text-emerald-700 border-emerald-200",
  deviation: "bg-amber-50 text-amber-800 border-amber-200",
  unknown: "bg-slate-50 text-slate-500 border-slate-200",
};
const SEV_STYLE = {
  critical: "bg-red-100 text-red-700",
  warning: "bg-amber-100 text-amber-700",
  info: "bg-slate-100 text-slate-600",
};

function RefChip({ ref_type, value, quote }) {
  const { t } = useApp();
  if (value == null || value === "") return null;
  const prefix = ref_type === "paragraph" ? t("ref_paragraph") : t("ref_page");
  return (
    <span
      title={quote || ""}
      data-testid="contract-ref-chip"
      className="inline-flex items-center gap-1 rounded-full bg-[#1B2A4A]/5 border border-[#1B2A4A]/15 px-2 py-0.5 text-[10px] font-mono text-[#1B2A4A] whitespace-nowrap cursor-help"
    >
      {quote ? <Quote className="h-2.5 w-2.5" /> : null}
      {prefix} {value}
    </span>
  );
}

function TermRow({ label, value, refType, refVal, quote }) {
  if (!value && refVal == null) return null;
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-gray-100 last:border-0">
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wide text-gray-400">{label}</p>
        <p className="text-sm text-gray-800 break-words">{value || "–"}</p>
      </div>
      <RefChip ref_type={refType} value={refVal} quote={quote} />
    </div>
  );
}

function ExtractionView({ doc }) {
  const { t } = useApp();
  const ex = doc.extraction || {};
  const rt = doc.ref_type;
  const devs = ex.deviations || [];
  const term = ex.term || {};
  const parties = ex.parties || {};
  const notice = ex.notice_period || {};
  const renewal = ex.auto_renewal || {};
  const pricing = ex.pricing || {};
  const pay = ex.payment_terms || {};
  const vol = ex.volume || {};
  const liab = ex.liability_warranty || {};
  const clauses = ex.price_index_clauses || [];

  return (
    <div className="space-y-4" data-testid="contract-extraction-view">
      {ex.summary && (
        <div className="rounded-lg border border-[#1B2A4A]/15 bg-[#1B2A4A]/[0.03] p-4">
          <TranslateBlock texts={[ex.summary]} testid="contract-summary-translate">
            {(display, button) => (
              <>
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-[#1B2A4A]/60 flex items-center gap-1.5">
                    <ScrollText className="h-3.5 w-3.5" />{t("contract_summary")}
                  </p>
                  {button}
                </div>
                <p className="text-sm text-gray-700 leading-relaxed" data-testid="contract-summary-text">{display[0]}</p>
              </>
            )}
          </TranslateBlock>
        </div>
      )}

      <div>
        <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 mb-2 flex items-center gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5" />{t("contract_deviations")}
        </p>
        {devs.length === 0 ? (
          <p className="text-sm text-gray-400" data-testid="no-deviations-msg">{t("no_deviations")}</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200" data-testid="deviations-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-200">
                  <th className="px-3 py-2 font-semibold">{t("field")}</th>
                  <th className="px-3 py-2 font-semibold">{t("contract_value")}</th>
                  <th className="px-3 py-2 font-semibold">{t("crm_value")}</th>
                  <th className="px-3 py-2 font-semibold">{t("status")}</th>
                </tr>
              </thead>
              <tbody>
                {devs.map((d, i) => (
                  <tr key={i} className="border-b border-gray-100 last:border-0" data-testid={`deviation-row-${i}`}>
                    <td className="px-3 py-2 align-top font-medium text-[#1B2A4A]">{d.field}</td>
                    <td className="px-3 py-2 align-top text-gray-700">{d.contract_value || "–"}</td>
                    <td className="px-3 py-2 align-top text-gray-700">{d.crm_value || "–"}</td>
                    <td className="px-3 py-2 align-top">
                      <div className="flex flex-col gap-1">
                        <Badge className={`w-fit border ${STATUS_STYLE[d.status] || STATUS_STYLE.unknown} hover:bg-inherit`}>
                          {d.status === "match" ? <CheckCircle2 className="h-3 w-3 mr-1" /> : d.status === "deviation" ? <AlertTriangle className="h-3 w-3 mr-1" /> : <HelpCircle className="h-3 w-3 mr-1" />}
                          {t(`deviation_${d.status}`)}
                        </Badge>
                        {d.severity && d.status === "deviation" && (
                          <span className={`w-fit text-[10px] px-1.5 py-0.5 rounded ${SEV_STYLE[d.severity] || ""}`}>{t(`severity_${d.severity}`)}</span>
                        )}
                        {d.note && <span className="text-[11px] text-gray-500">{d.note}</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 mb-2">{t("extracted_terms")}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 rounded-lg border border-gray-200 px-4">
          <TermRow label={t("contract_parties")} value={[parties.provider, parties.customer].filter(Boolean).join(" ↔ ")} refType={rt} refVal={parties.ref} quote={parties.quote} />
          <TermRow label={t("contract_term")} value={[term.start_date, term.end_date].filter(Boolean).join(" – ") + (term.duration ? ` (${term.duration})` : "")} refType={rt} refVal={term.ref} quote={term.quote} />
          <TermRow label={t("notice_period")} value={notice.text || (notice.months ? `${notice.months} ${t("months")}` : "")} refType={rt} refVal={notice.ref} quote={notice.quote} />
          <TermRow label={t("auto_renewal")} value={renewal.exists ? (renewal.renewal_term || renewal.text || t("yes")) : t("no")} refType={rt} refVal={renewal.ref} quote={renewal.quote} />
          <TermRow label={t("pricing_conditions")} value={pricing.text} refType={rt} refVal={pricing.ref} quote={pricing.quote} />
          <TermRow label={t("payment_terms")} value={pay.text || (pay.days ? `${pay.days} ${t("days")}` : "")} refType={rt} refVal={pay.ref} quote={pay.quote} />
          <TermRow label={t("volume_minimum")} value={[vol.minimum, vol.text].filter(Boolean).join(" · ")} refType={rt} refVal={vol.ref} quote={vol.quote} />
          <TermRow label={t("liability_warranty")} value={liab.text} refType={rt} refVal={liab.ref} quote={liab.quote} />
        </div>
      </div>

      {clauses.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-widest text-gray-400 mb-2">{t("price_index_clauses")}</p>
          <div className="space-y-2">
            {clauses.map((c, i) => (
              <div key={i} className="rounded-lg border border-gray-200 p-3 flex items-start justify-between gap-3" data-testid={`index-clause-${i}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#1B2A4A]">{c.index_name || "–"}</p>
                  <p className="text-xs text-gray-600">
                    {c.base_value ? `${t("index_base_value")}: ${c.base_value}` : ""} {c.threshold_pct ? ` · ±${c.threshold_pct}%` : ""}
                  </p>
                  {c.text && <p className="text-xs text-gray-500 mt-1">{c.text}</p>}
                </div>
                <RefChip ref_type={rt} value={c.ref} quote={c.quote} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function ContractDocumentsPanel({ contract }) {
  const { t, canWrite } = useApp();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [selLoading, setSelLoading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/contracts/${contract.id}/documents`)
      .then((r) => setDocs(r.data.documents || []))
      .catch((e) => toast.error(errDetail(e)))
      .finally(() => setLoading(false));
  }, [contract.id]);

  useEffect(() => { load(); }, [load]);

  const openDoc = async (id) => {
    setSelLoading(true);
    try {
      const r = await api.get(`/contract-documents/${id}`);
      setSelected(r.data);
    } catch (e) { toast.error(errDetail(e)); }
    finally { setSelLoading(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["pdf", "docx"].includes(ext)) { toast.error(t("only_pdf_docx")); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/contracts/${contract.id}/documents`, fd);
      toast.success(t("extraction_done"));
      await load();
      setSelected(r.data);
    } catch (e) {
      toast.error(errDetail(e));
      await load();
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const del = async (id) => {
    if (!window.confirm(t("confirm_delete_doc"))) return;
    try {
      await api.delete(`/contract-documents/${id}`);
      if (selected?.id === id) setSelected(null);
      await load();
      toast.success(t("deleted"));
    } catch (e) { toast.error(errDetail(e)); }
  };

  const reextract = async (id) => {
    setSelLoading(true);
    try {
      const r = await api.post(`/contract-documents/${id}/reextract`);
      setSelected(r.data);
      await load();
      toast.success(t("extraction_done"));
    } catch (e) { toast.error(errDetail(e)); }
    finally { setSelLoading(false); }
  };

  if (selected) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-2">
          <Button variant="ghost" size="sm" onClick={() => setSelected(null)} data-testid="back-to-docs-btn">
            <ChevronLeft className="h-4 w-4 mr-1" />{t("back")}
          </Button>
          <div className="flex items-center gap-2">
            <a href={`${api.defaults.baseURL}/files/download/${selected.file_id}?auth=${localStorage.getItem("lf_token")}`}
              target="_blank" rel="noreferrer" data-testid="view-original-btn">
              <Button variant="outline" size="sm"><FileText className="h-3.5 w-3.5 mr-1" />{t("view_original")}</Button>
            </a>
            {canWrite && (
              <Button variant="outline" size="sm" disabled={selLoading} onClick={() => reextract(selected.id)} data-testid="reextract-btn">
                <RefreshCw className={`h-3.5 w-3.5 mr-1 ${selLoading ? "animate-spin" : ""}`} />{t("reextract")}
              </Button>
            )}
          </div>
        </div>
        <p className="text-sm font-medium text-gray-700 truncate">{selected.original_filename}</p>
        {selLoading ? (
          <div className="py-10 text-center text-gray-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : selected.extraction_status === "done" ? (
          <ExtractionView doc={selected} />
        ) : (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="extraction-failed">
            {t("extraction_failed")}: {selected.extraction_error || ""}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {canWrite && (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold rounded-full"
            data-testid="upload-contract-doc-btn"
          >
            {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
            {uploading ? t("analyzing") : t("upload_contract_doc")}
          </Button>
          <span className="text-xs text-gray-400">{t("only_pdf_docx")} · max. 20 MB</span>
          <input ref={fileRef} type="file" accept=".pdf,.docx" className="hidden"
            onChange={(e) => upload(e.target.files?.[0])} data-testid="contract-doc-file-input" />
        </div>
      )}
      {uploading && (
        <div className="rounded-lg border border-dashed border-[#F5A623]/50 bg-amber-50/40 p-4 text-sm text-gray-600 flex items-center gap-2" data-testid="analyzing-banner">
          <Loader2 className="h-4 w-4 animate-spin text-[#F5A623]" />{t("analyzing_hint")}
        </div>
      )}

      {loading ? (
        <div className="py-10 text-center text-gray-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
      ) : docs.length === 0 ? (
        <div className="py-10 text-center" data-testid="no-contract-docs">
          <FileText className="h-10 w-10 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">{t("no_contract_docs")}</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="contract-docs-list">
          {docs.map((d) => (
            <div key={d.id}
              className="flex items-center gap-3 rounded-lg border border-gray-200 p-3 hover:bg-amber-50/40 transition-colors cursor-pointer"
              onClick={() => openDoc(d.id)} data-testid={`contract-doc-${d.id}`}>
              <FileText className="h-5 w-5 text-[#1B2A4A] shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-800 truncate">{d.original_filename}</p>
                <p className="text-xs text-gray-400">
                  {fmtDate(d.created_at)} · {d.uploaded_by}
                  {d.page_count ? ` · ${d.page_count} ${d.ref_type === "paragraph" ? t("paragraphs") : t("pages")}` : ""}
                </p>
              </div>
              {d.extraction_status === "done" ? (
                <div className="flex items-center gap-1.5 shrink-0">
                  {d.critical_count > 0 && (
                    <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
                      <AlertTriangle className="h-3 w-3 mr-1" />{d.critical_count}
                    </Badge>
                  )}
                  {d.deviation_count > 0 ? (
                    <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100" data-testid={`doc-deviations-${d.id}`}>
                      {d.deviation_count} {t("deviations_short")}
                    </Badge>
                  ) : (
                    <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50">
                      <CheckCircle2 className="h-3 w-3 mr-1" />{t("ok")}
                    </Badge>
                  )}
                </div>
              ) : (
                <Badge className="bg-slate-100 text-slate-500 hover:bg-slate-100">{d.extraction_status}</Badge>
              )}
              {canWrite && (
                <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0"
                  onClick={(e) => { e.stopPropagation(); del(d.id); }} data-testid={`delete-doc-${d.id}`}>
                  <Trash2 className="h-4 w-4 text-gray-400" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ContractDocumentsDialog({ contract, onClose }) {
  const { t } = useApp();
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="contract-documents-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-[#1B2A4A]" />
            {t("ai_contract_analysis")} – {contract.contract_number}
          </DialogTitle>
          <DialogDescription>{contract.name} · {contract.account_name}</DialogDescription>
        </DialogHeader>
        <ContractDocumentsPanel contract={contract} />
      </DialogContent>
    </Dialog>
  );
}
